"use strict";
/*
 * Follow-up question suggestions for the chat composer.
 *
 * After a Claude chat turn finishes, the browser asks the hub for ONE likely
 * next question. The hub builds a small prompt here and hands it to the
 * tenant's own agent (a `followup` op), which runs it through a fast model
 * (Haiku) on the user's own Claude install — the hub never calls a model itself.
 * The browser shows the result as ghost text in the empty input; Tab accepts it.
 *
 * Pure functions only, so they are unit-testable without an agent.
 */

const MAX_QUESTION_CHARS = 2000;
const MAX_ANSWER_CHARS = 4000;
const MAX_SUGGESTION_CHARS = 200;

// Keep the head of the user's question (intent lives there) and the TAIL of the
// answer (the conclusion / open ends a follow-up usually builds on).
function clip(s, n, fromEnd) {
  const str = String(s == null ? "" : s).trim();
  if (str.length <= n) return str;
  return fromEnd ? "…" + str.slice(str.length - n) : str.slice(0, n) + "…";
}

function buildFollowupPrompt({ question, answer }) {
  const q = clip(question, MAX_QUESTION_CHARS, false);
  const a = clip(answer, MAX_ANSWER_CHARS, true);
  return [
    "You suggest the user's NEXT message in a chat with an AI coding assistant.",
    "Read the last exchange below and write the single most useful follow-up the user is likely to send next.",
    "Rules:",
    "- Write it as the user, in first person, as a short question or request (max ~15 words).",
    "- Use the SAME language as the user's message (e.g. Hebrew in, Hebrew out).",
    "- Be specific to this exchange; no generic \"tell me more\".",
    "- If the assistant asked the user something or offered options, suggest the natural reply (e.g. a short \"yes, go ahead\").",
    "- Output ONLY the suggestion text: no quotes, no preamble, no explanation, a single line.",
    "- If nothing sensible fits, output exactly: NONE",
    "",
    "<user_message>",
    q,
    "</user_message>",
    "",
    "<assistant_reply>",
    a,
    "</assistant_reply>",
  ].join("\n");
}

// Normalise whatever the model printed into one clean line (or "" for none).
function cleanFollowup(raw) {
  let s = String(raw == null ? "" : raw).replace(/\r/g, "").trim();
  if (!s) return "";
  // First non-empty line only; the model occasionally adds an explanation.
  s = s.split("\n").map((l) => l.trim()).filter(Boolean)[0] || "";
  s = s.replace(/^(suggestion|follow-?up|next message)\s*:\s*/i, "");
  // Strip one layer of wrapping quotes/backticks (ASCII, curly, Hebrew geresh).
  s = s.replace(/^["'`“”‘’״]+|["'`“”‘’״]+$/g, "").trim();
  if (!s || /^none\.?$/i.test(s)) return "";
  if (s.length > MAX_SUGGESTION_CHARS) s = s.slice(0, MAX_SUGGESTION_CHARS).trim() + "…";
  return s;
}

// Follow-up ops require a v2.10.0+ agent. Older agents treat an unknown job kind
// as a full chat run, so the hub must never dispatch this op to them.
function agentSupportsFollowup(a) {
  const p = String((a && a.version) || "0").split(".").map((n) => parseInt(n, 10) || 0);
  return p[0] > 2 || (p[0] === 2 && p[1] >= 10);
}

// Inline follow-ups (the MAIN chat turn emits its own suggestion, no extra
// Haiku call) require a v2.11.0+ agent: older agents ignore job.followupHint,
// so the flag is only sent when the agent understands it.
function agentSupportsInlineFollowup(a) {
  const p = String((a && a.version) || "0").split(".").map((n) => parseInt(n, 10) || 0);
  return p[0] > 2 || (p[0] === 2 && p[1] >= 11);
}

// ---- inline follow-up marker ------------------------------------------------
// With job.followupHint the agent appends INLINE_FOLLOWUP_INSTRUCTION to the
// main turn's system prompt, so the answer ends with a line like
//   <<followup: can you add tests for it?>>
// These helpers are pure and shared VERBATIM with the browser: src/app.jsx
// imports this file (esbuild bundles it), so there is exactly one copy.
const INLINE_FOLLOWUP_INSTRUCTION =
  "Hub UI requirement: end every reply with one extra line <<followup: X>> where X is the single most likely " +
  "next message the user will send you (first person, max ~15 words, same language as the user, specific " +
  "to this exchange; if you asked the user something or offered options, the natural reply). " +
  "Write it on its own line, AFTER everything else, including any summary or status block other " +
  "instructions ask you to append. The UI hides it, so never mention it. Omit it only if nothing sensible fits.";

// A complete marker at the very end of the text (optional wrapping backticks,
// trailing whitespace allowed, case-insensitive).
const FOLLOWUP_MARKER_END_RE = /`?<<\s*followup\s*:\s*([^\n]*?)\s*>>`?\s*$/i;
// Any complete marker anywhere (a model that kept writing after it).
const FOLLOWUP_MARKER_ANY_RE = /[ \t]*`?<<\s*followup\s*:[^\n]*?>>`?/gi;

// Split a finished answer into its visible text and the suggested next message.
// Only a marker at the END yields a suggestion; a stray marker mid-text is still
// removed from the visible text but ignored as a suggestion.
// A marker that sits on its own line anywhere in the text. Other instructions
// (e.g. a user CLAUDE.md "summary block at the very end") can push it off the
// last line, so the LAST own-line marker wins; an inline mention mid-sentence
// is only removed, never used.
const FOLLOWUP_MARKER_LINE_RE = /^[ \t]*`?<<\s*followup\s*:\s*([^\n]*?)\s*>>`?[ \t]*$/gim;
function extractInlineFollowup(text) {
  const src = String(text == null ? "" : text);
  if (src.indexOf("<<") < 0) return { text: src, suggestion: "" };
  let suggestion = "";
  let out = src;
  const m = FOLLOWUP_MARKER_END_RE.exec(out);
  if (m) {
    suggestion = cleanFollowup(m[1]);
    out = out.slice(0, m.index).replace(/\s+$/, "");
  } else {
    let last = null, mm;
    FOLLOWUP_MARKER_LINE_RE.lastIndex = 0;
    while ((mm = FOLLOWUP_MARKER_LINE_RE.exec(out))) last = mm;
    if (last) {
      suggestion = cleanFollowup(last[1]);
      out = out.slice(0, last.index) + out.slice(last.index + last[0].length);
      out = out.replace(/\n{3,}/g, "\n\n").replace(/\s+$/, "");
    }
  }
  if (/<<\s*followup\s*:/i.test(out)) out = out.replace(FOLLOWUP_MARKER_ANY_RE, "").replace(/[ \t]+\n/g, "\n").replace(/\s+$/, "");
  return { text: out, suggestion };
}

// While streaming, the marker arrives in pieces ("<", "<<fol", "<<followup: ab").
// Hide a trailing piece that could still become a marker so it never flashes.
const FOLLOWUP_PREFIX_RE = /^`?<(?:<\s*(?:f(?:o(?:l(?:l(?:o(?:w(?:u(?:p(?:\s*:[^\n>]*(?:>)?)?)?)?)?)?)?)?)?)?)?$/i;
function stripPartialFollowupMarker(text) {
  const src = String(text == null ? "" : text);
  const i = src.lastIndexOf("<");
  if (i < 0) return src;
  // The candidate starts at the FIRST "<" of a trailing "<<" (or a lone "<").
  let start = i > 0 && src[i - 1] === "<" ? i - 1 : i;
  if (start > 0 && src[start - 1] === "`") start--;
  const tail = src.slice(start);
  if (!FOLLOWUP_PREFIX_RE.test(tail)) {
    // "<<followup: some words" - the last "<" is inside the marker's head.
    const j = src.lastIndexOf("<<");
    if (j < 0) return src;
    const k = j > 0 && src[j - 1] === "`" ? j - 1 : j;
    if (!/^`?<<\s*followup\s*:[^\n>]*>?$/i.test(src.slice(k))) return src;
    return src.slice(0, k).replace(/\s+$/, "");
  }
  return src.slice(0, start).replace(/\s+$/, "");
}

// Display form of assistant text: never shows a complete marker; while the
// turn is still streaming (`live`) also hides a partial trailing one.
function stripFollowupForDisplay(text, live) {
  const out = extractInlineFollowup(text).text;
  return live ? stripPartialFollowupMarker(out) : out;
}

// Clean every text part (and meta.resultText) of one assistant message. Returns
// the SAME object when nothing changed so callers can cheaply skip re-renders /
// writes, plus the suggestion from the last text part that carried one.
function stripFollowupFromMessage(m) {
  if (!m || typeof m !== "object" || m.role !== "assistant") return { message: m, suggestion: "" };
  let changed = false, suggestion = "";
  let parts = m.parts;
  if (Array.isArray(parts)) {
    parts = parts.map((pt) => {
      if (!pt || pt.type !== "text" || typeof pt.text !== "string" || pt.text.indexOf("<<") < 0) return pt;
      const r = extractInlineFollowup(pt.text);
      if (r.suggestion) suggestion = r.suggestion;
      if (r.text === pt.text) return pt;
      changed = true;
      return { ...pt, text: r.text };
    });
  }
  let meta = m.meta;
  if (meta && typeof meta.resultText === "string" && meta.resultText.indexOf("<<") >= 0) {
    const r = extractInlineFollowup(meta.resultText);
    if (r.text !== meta.resultText) { meta = { ...meta, resultText: r.text }; changed = true; }
    if (!suggestion && r.suggestion) suggestion = r.suggestion;
  }
  let text = m.text;
  if (typeof text === "string" && text.indexOf("<<") >= 0) {
    const r = extractInlineFollowup(text);
    if (r.text !== text) { text = r.text; changed = true; }
  }
  if (!changed) return { message: m, suggestion };
  const out = { ...m };
  if (parts !== m.parts) out.parts = parts;
  if (meta !== m.meta) out.meta = meta;
  if (text !== m.text) out.text = text;
  return { message: out, suggestion };
}

// Clean a whole session (every assistant message). Same object when unchanged.
function stripFollowupFromSession(s) {
  if (!s || typeof s !== "object" || !Array.isArray(s.messages)) return s;
  let changed = false;
  const messages = s.messages.map((m) => {
    const r = stripFollowupFromMessage(m);
    if (r.message !== m) changed = true;
    return r.message;
  });
  return changed ? { ...s, messages } : s;
}

module.exports = {
  buildFollowupPrompt, cleanFollowup, agentSupportsFollowup, agentSupportsInlineFollowup,
  INLINE_FOLLOWUP_INSTRUCTION, extractInlineFollowup, stripPartialFollowupMarker,
  stripFollowupForDisplay, stripFollowupFromMessage, stripFollowupFromSession,
  MAX_QUESTION_CHARS, MAX_ANSWER_CHARS, MAX_SUGGESTION_CHARS,
};
